import { useEffect, useRef, useState } from "react";
import { Spin } from "antd";

const DEFAULT_LOCATION = { lat: 40.7128, lng: -74.006 };
const DEFAULT_ZOOM = 3;
const GOOGLE_SCRIPT_ID = "google-maps-plate-picker";
const COORD_PRECISION = 6;

export function roundCoord(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return num;
  return Number(num.toFixed(COORD_PRECISION));
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

let googleMapsLoadPromise = null;

function isValidCoord(lat, lng) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  if (la < -90 || la > 90 || lo < -180 || lo > 180) return false;
  return la !== 0 || lo !== 0;
}

export function resolvePlateMapLocation(latitude, longitude) {
  return isValidCoord(latitude, longitude)
    ? { lat: Number(latitude), lng: Number(longitude) }
    : DEFAULT_LOCATION;
}

function resolveLocation(latitude, longitude) {
  return resolvePlateMapLocation(latitude, longitude);
}

function waitForGoogleMaps(maxAttempts = 100, intervalMs = 100) {
  return new Promise((resolve, reject) => {
    const poll = (attempt) => {
      if (window.google?.maps) {
        resolve(window.google);
        return;
      }
      if (attempt >= maxAttempts) {
        reject(new Error("Google Maps API did not initialize in time."));
        return;
      }
      setTimeout(() => poll(attempt + 1), intervalMs);
    };
    poll(0);
  });
}

function loadGoogleMapsApi(apiKey) {
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  const existing = document.getElementById(GOOGLE_SCRIPT_ID);
  if (existing) {
    googleMapsLoadPromise = waitForGoogleMaps().catch((err) => {
      googleMapsLoadPromise = null;
      throw err;
    });
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${encodeURIComponent(apiKey)}`;
    script.onload = () => {
      waitForGoogleMaps().then(resolve).catch(reject);
    };
    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error("Failed to load the Google Maps script."));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

export const PlateMapPicker = ({ latitude, longitude, onLocationChange }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onLocationChangeRef = useRef(onLocationChange);

  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState(null);
  const [location, setLocation] = useState(() =>
    resolveLocation(latitude, longitude),
  );

  onLocationChangeRef.current = onLocationChange;

  useEffect(() => {
    setLocation(resolveLocation(latitude, longitude));
  }, [latitude, longitude]);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;

    let active = true;
    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (active) setMapsReady(true);
      })
      .catch((err) => {
        if (active) setMapsError(err.message || "Failed to load Google Maps.");
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current || !window.google?.maps) {
      return undefined;
    }

    const initial = resolveLocation(latitude, longitude);
    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: initial,
      zoom: DEFAULT_ZOOM,
      mapTypeId: "roadmap",
    });
    const marker = new window.google.maps.Marker({
      position: initial,
      map,
      draggable: true,
    });

    mapRef.current = map;
    markerRef.current = marker;
    setLocation(initial);
    onLocationChangeRef.current?.(initial.lat, initial.lng);

    const syncLocation = (lat, lng) => {
      const next = { lat: roundCoord(lat), lng: roundCoord(lng) };
      setLocation(next);
      onLocationChangeRef.current?.(next.lat, next.lng);
    };

    const dragListener = marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      if (pos) syncLocation(pos.lat(), pos.lng());
    });

    const clickListener = map.addListener("click", (event) => {
      const clicked = event.latLng;
      if (!clicked) return;
      marker.setPosition(clicked);
      syncLocation(clicked.lat(), clicked.lng());
    });

    return () => {
      window.google.maps.event.removeListener(dragListener);
      window.google.maps.event.removeListener(clickListener);
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [mapsReady, latitude, longitude]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="text-red-500 text-sm p-2">
        Google Maps API key is not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY
        in your environment.
      </div>
    );
  }

  if (mapsError) {
    return <div className="text-red-500 text-sm p-2">{mapsError}</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2 text-sm text-black">
        <span>
          <span className="font-medium">Latitude:</span> {location.lat}
        </span>
        <span>
          <span className="font-medium">Longitude:</span> {location.lng}
        </span>
      </div>
      <div className="relative" style={{ height: "400px", width: "100%" }}>
        {!mapsReady ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <Spin tip="Loading map..." />
          </div>
        ) : null}
        <div
          ref={mapContainerRef}
          style={{ height: "400px", width: "100%" }}
        />
      </div>
    </div>
  );
};
